import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Plus, Link2, FileDown, Trash2, Eye, X, FileSignature, Landmark, Mail } from "lucide-react";
import { Field } from "@/components/admin/EditorControls";
import { ContractDocument } from "@/components/contract/ContractDocument";

const statusBadge = (s) =>
  s === "signed"
    ? { label: "締結済", cls: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30" }
    : { label: "署名待ち", cls: "bg-amber-500/15 text-amber-300 border-amber-500/30" };

const SETTING_KEYS = ["payment_method", "bank_name", "branch_name", "account_type", "account_number", "account_holder"];

export function ContractsPanel({ onChanged }) {
  const [tpl, setTpl] = useState(null);
  const [list, setList] = useState(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [newLink, setNewLink] = useState(null);
  const [detail, setDetail] = useState(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [settingsForm, setSettingsForm] = useState(null);
  const [savingSettings, setSavingSettings] = useState(false);
  const [confirmDel, setConfirmDel] = useState(null);
  const [mailFor, setMailFor] = useState(null);

  const load = () => api.get("/contracts").then((r) => setList(r.data)).catch(() => setList([]));
  useEffect(() => {
    api.get("/contract/template").then((r) => setTpl(r.data)).catch(() => {});
    api.get("/contracts/settings").then((r) => setSettingsForm({ ...(r.data?.fields || {}) })).catch(() => {});
    load();
  }, []);

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      await api.put("/contracts/settings", { fields: settingsForm });
      const r = await api.get("/contract/template");
      setTpl(r.data);
      toast.success("既定値を保存しました。新規作成する契約に自動で記載されます");
      setSettingsOpen(false);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSavingSettings(false);
    }
  };

  const startCreate = () => {
    setForm({ ...(tpl?.defaults || {}) });
    setCreating(true);
    setNewLink(null);
  };

  const submit = async () => {
    setBusy(true);
    try {
      const r = await api.post("/contracts", { fields: form });
      setNewLink(`${window.location.origin}${r.data.sign_path}`);
      setCreating(false);
      toast.success("契約を作成しました。署名用URLを相手方にお送りください");
      load();
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const copy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success("署名用URLをコピーしました");
    } catch {
      toast.error("コピーに失敗しました");
    }
  };

  const downloadPdf = async (id) => {
    try {
      const r = await api.get(`/contracts/${id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `contract_${id.slice(0, 8)}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("PDFの生成に失敗しました");
    }
  };

  const openDetail = async (id) => {
    try {
      const r = await api.get(`/contracts/${id}`);
      setDetail(r.data);
    } catch {
      toast.error("読み込みに失敗しました");
    }
  };

  const del = async (id) => {
    try {
      await api.delete(`/contracts/${id}`);
      toast.success("削除しました");
      setDetail(null);
      setConfirmDel(null);
      load();
      onChanged?.();
    } catch {
      toast.error("削除に失敗しました");
    }
  };

  return (
    <div data-testid="contracts-panel">
      <div className="flex items-center justify-between mb-5 gap-3 flex-wrap">
        <p className="text-sm text-white/50">契約を作成すると署名用URLが発行されます。URLを相手方にお送りください。</p>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setSettingsOpen((o) => !o)}
            data-testid="contract-settings-toggle"
            className={`inline-flex items-center gap-2 rounded-lg border px-4 py-2 text-sm transition-colors ${settingsOpen ? "border-gold/60 text-gold bg-gold/10" : "border-white/15 text-white/70 hover:text-gold hover:border-gold/40"}`}
          >
            <Landmark size={15} /> 支払方法・振込先の既定値
          </button>
          <button
            onClick={startCreate}
            data-testid="contract-create-open"
            className="inline-flex items-center gap-2 rounded-lg bg-gold px-4 py-2 text-sm font-medium text-ink hover:bg-gold-light transition-colors"
          >
            <Plus size={15} /> 新規契約を作成
          </button>
        </div>
      </div>

      {settingsOpen && settingsForm && (
        <div className="mb-5 rounded-xl border border-gold/30 bg-gold/[0.03] p-5" data-testid="contract-settings-form">
          <p className="text-xs text-white/40 mb-3">ここで保存した内容が、新規作成するすべての契約書の別紙2（支払方法・振込先）に自動で記載されます。</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {SETTING_KEYS.map((k) => {
              const d = (tpl?.field_defs || []).find((f) => f.key === k);
              return (
                <Field
                  key={k}
                  label={d?.label || k}
                  value={settingsForm[k] ?? ""}
                  onChange={(v) => setSettingsForm((f) => ({ ...f, [k]: v }))}
                  placeholder={d?.placeholder}
                  testid={`cs-${k}`}
                />
              );
            })}
          </div>
          <button
            onClick={saveSettings}
            disabled={savingSettings}
            data-testid="contract-settings-save"
            className="mt-4 inline-flex items-center gap-2 rounded-lg bg-gold px-5 py-2.5 text-sm font-medium text-ink hover:bg-gold-light transition-colors disabled:opacity-60"
          >
            {savingSettings && <Loader2 size={15} className="animate-spin" />} 既定値を保存
          </button>
        </div>
      )}

      {newLink && (
        <div className="mb-5 rounded-xl border border-emerald-400/30 bg-emerald-400/10 p-4" data-testid="new-contract-link">
          <p className="text-sm font-bold text-emerald-200 mb-2">署名用URLが発行されました（相手方にメール等で送付してください）</p>
          <div className="flex items-center gap-2">
            <input readOnly value={newLink} className="flex-1 bg-black/30 border border-white/15 rounded-lg px-3 py-2 text-xs text-white/80" data-testid="new-contract-url" />
            <button onClick={() => copy(newLink)} data-testid="copy-new-link" className="inline-flex items-center gap-1.5 rounded-lg border border-emerald-400/40 text-emerald-300 px-3 py-2 text-xs hover:bg-emerald-400/10 transition-colors">
              <Link2 size={14} /> コピー
            </button>
          </div>
        </div>
      )}

      {creating && form && (
        <div className="mb-6 rounded-xl border border-gold/30 bg-gold/[0.03] p-5" data-testid="contract-create-form">
          <div className="flex items-center justify-between mb-4">
            <p className="font-jp font-bold text-gold">新規契約（可変項目を入力）</p>
            <button onClick={() => setCreating(false)} className="text-white/50 hover:text-white"><X size={18} /></button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {(tpl?.field_defs || []).map((d) => (
              <Field
                key={d.key}
                label={`${d.label}${d.required ? " *" : ""}`}
                value={form[d.key] ?? ""}
                onChange={(v) => setForm((f) => ({ ...f, [d.key]: v }))}
                placeholder={d.placeholder}
                testid={`cf-${d.key}`}
              />
            ))}
          </div>
          <p className="text-xs text-white/40 mt-3">本文（第1条〜第31条）・別紙の条項文はテンプレート固定です。住所・肩書・代表者氏名・電話番号は相手方が署名時に入力します。</p>
          <button
            onClick={submit}
            disabled={busy}
            data-testid="contract-create-submit"
            className="mt-4 inline-flex items-center gap-2 rounded-lg bg-gold px-5 py-2.5 text-sm font-medium text-ink hover:bg-gold-light transition-colors disabled:opacity-60"
          >
            {busy ? <Loader2 size={15} className="animate-spin" /> : <FileSignature size={15} />} 作成して署名用URLを発行
          </button>
        </div>
      )}

      {list === null ? (
        <div className="py-16 flex justify-center"><Loader2 className="animate-spin text-gold" /></div>
      ) : list.length === 0 ? (
        <p className="py-16 text-center text-white/40 text-sm" data-testid="contracts-empty">まだ契約はありません。</p>
      ) : (
        <div className="space-y-3" data-testid="contracts-list">
          {list.map((c) => {
            const b = statusBadge(c.status);
            return (
              <div key={c.id} className="rounded-xl border border-white/10 bg-white/[0.02] p-4 flex flex-wrap items-center gap-4" data-testid={`contract-row-${c.id}`}>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-jp font-bold text-white">{c.fields?.client_company || "（会社名なし）"}</p>
                    <span className={`rounded border px-1.5 py-0.5 text-[10px] ${b.cls}`}>{b.label}</span>
                  </div>
                  <p className="text-xs text-white/40 mt-1 truncate">{c.fields?.target_site}</p>
                  <p className="text-xs text-white/30 mt-0.5">
                    作成: {new Date(c.created_at).toLocaleString("ja-JP")}
                    {c.signer?.signed_at_jp && <>　/　締結: {c.signer.signed_at_jp}（{c.signer.rep_name}）</>}
                  </p>
                </div>
                <div className="flex items-center gap-1.5">
                  <button onClick={() => copy(`${window.location.origin}/contract/${c.token}`)} title="署名用URLをコピー" data-testid={`contract-copy-${c.id}`} className="p-2 rounded-lg border border-white/10 text-white/60 hover:text-gold hover:border-gold/40 transition-colors"><Link2 size={15} /></button>
                  <button onClick={() => setMailFor(c)} title="メールで送付" data-testid={`contract-mail-${c.id}`} className="p-2 rounded-lg border border-white/10 text-white/60 hover:text-gold hover:border-gold/40 transition-colors"><Mail size={15} /></button>
                  <button onClick={() => openDetail(c.id)} title="内容を表示" data-testid={`contract-view-${c.id}`} className="p-2 rounded-lg border border-white/10 text-white/60 hover:text-gold hover:border-gold/40 transition-colors"><Eye size={15} /></button>
                  <button onClick={() => downloadPdf(c.id)} title="PDFダウンロード" data-testid={`contract-pdf-${c.id}`} className="p-2 rounded-lg border border-white/10 text-white/60 hover:text-gold hover:border-gold/40 transition-colors"><FileDown size={15} /></button>
                  <button onClick={() => setConfirmDel(c)} title="削除" data-testid={`contract-delete-${c.id}`} className="p-2 rounded-lg border border-white/10 text-white/60 hover:text-red-400 hover:border-red-400/40 transition-colors"><Trash2 size={15} /></button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {mailFor && (
        <ContractMailModal
          contract={mailFor}
          onClose={() => setMailFor(null)}
        />
      )}

      {confirmDel && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" onClick={() => setConfirmDel(null)} data-testid="delete-confirm-modal">
          <div className="w-full max-w-md rounded-2xl border border-white/10 bg-surface p-8" onClick={(e) => e.stopPropagation()}>
            <p className="font-jp text-lg font-bold text-white">この契約を削除しますか？</p>
            <p className="mt-3 text-sm text-white/60">
              {confirmDel.fields?.client_company || "（会社名なし）"} との契約（{confirmDel.status === "signed" ? "締結済み" : "署名待ち"}）を削除します。
            </p>
            {confirmDel.status === "signed" && (
              <p className="mt-2 text-xs text-red-300/90">締結済みの契約を削除すると、署名・写真・身分証明書などの記録もすべて失われ、元に戻せません。</p>
            )}
            <div className="mt-6 flex gap-3">
              <button onClick={() => del(confirmDel.id)} data-testid="delete-confirm-yes" className="flex-1 rounded-lg bg-red-500/90 px-4 py-2.5 text-sm font-bold text-white hover:bg-red-500 transition-colors">削除する</button>
              <button onClick={() => setConfirmDel(null)} data-testid="delete-confirm-cancel" className="flex-1 rounded-lg border border-white/15 px-4 py-2.5 text-sm text-white/70 hover:text-white transition-colors">キャンセル</button>
            </div>
          </div>
        </div>
      )}

      {detail && (
        <div className="fixed inset-0 z-40 flex items-start justify-center bg-black/70 backdrop-blur-sm p-4 overflow-y-auto" onClick={() => setDetail(null)} data-testid="contract-detail-modal">
          <div className="w-full max-w-4xl my-8" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <p className="font-jp font-bold text-white">{detail.fields?.client_company} 御中との契約</p>
              <button onClick={() => setDetail(null)} className="text-white/50 hover:text-white" data-testid="contract-detail-close"><X size={20} /></button>
            </div>
            <ContractDocument
              template={tpl?.template}
              ctx={detail.ctx}
              signer={detail.signer}
              signatureB64={detail.signature_b64}
              photoB64={detail.photo_b64}
              idFrontB64={detail.id_front_b64}
              idBackB64={detail.id_back_b64}
              sealB64={detail.seal_b64}
            />
          </div>
        </div>
      )}
    </div>
  );
}

function ContractMailModal({ contract, onClose }) {
  const [to, setTo] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState("");
  const signed = contract.status === "signed";

  const send = async (kind) => {
    if (!to.trim()) { toast.error("送信先メールアドレスを入力してください"); return; }
    setBusy(kind);
    try {
      const r = await api.post(`/contracts/${contract.id}/${kind}`, { to: to.trim(), message });
      toast.success(`${r.data.to} へ送信しました`);
      onClose();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy("");
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" onClick={onClose} data-testid="contract-mail-modal">
      <div className="w-full max-w-lg rounded-2xl border border-white/10 bg-surface p-7" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-5">
          <div>
            <p className="text-[10px] tracking-[0.2em] text-gold/80 font-mono uppercase">Send by Mail</p>
            <p className="font-jp text-lg font-bold text-white mt-1">{contract.fields?.client_company || "（会社名なし）"} への契約メール送付</p>
          </div>
          <button onClick={onClose} data-testid="contract-mail-close" className="text-white/40 hover:text-white transition-colors"><X size={20} /></button>
        </div>
        <div className="space-y-4">
          <div>
            <label className="block text-xs text-white/50 mb-1.5">送信先メールアドレス *</label>
            <input
              type="email"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              placeholder="client@example.com"
              data-testid="contract-mail-to"
              className="w-full rounded-lg border border-white/10 bg-black/30 px-3.5 py-2.5 text-sm text-white placeholder:text-white/30 focus:border-gold/50 outline-none transition-colors"
            />
          </div>
          <div>
            <label className="block text-xs text-white/50 mb-1.5">添え書き（任意）</label>
            <textarea
              rows={3}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="メール本文に追記するメッセージ"
              data-testid="contract-mail-message"
              className="w-full rounded-lg border border-white/10 bg-black/30 px-3.5 py-2.5 text-sm text-white placeholder:text-white/30 focus:border-gold/50 outline-none transition-colors resize-y"
            />
          </div>
          <p className="text-[11px] text-white/35">送信元: info@prcremit.co.jp</p>
        </div>
        <div className="mt-6 flex flex-col gap-2.5">
          {!signed && (
            <button onClick={() => send("send-link")} disabled={!!busy} data-testid="contract-mail-send-link" className="inline-flex items-center justify-center gap-2 rounded-lg bg-gold px-5 py-2.5 text-sm font-bold text-ink hover:bg-gold-light transition-colors disabled:opacity-60">
              {busy === "send-link" ? <Loader2 size={14} className="animate-spin" /> : <Link2 size={14} />} 署名用URLをメール送信
            </button>
          )}
          {signed && (
            <button onClick={() => send("send-pdf")} disabled={!!busy} data-testid="contract-mail-send-pdf" className="inline-flex items-center justify-center gap-2 rounded-lg bg-gold px-5 py-2.5 text-sm font-bold text-ink hover:bg-gold-light transition-colors disabled:opacity-60">
              {busy === "send-pdf" ? <Loader2 size={14} className="animate-spin" /> : <FileDown size={14} />} 締結済みPDFをメール送信
            </button>
          )}
          <button onClick={onClose} className="rounded-lg border border-white/15 px-5 py-2.5 text-sm text-white/70 hover:text-white transition-colors">キャンセル</button>
        </div>
      </div>
    </div>
  );
}
